import React, { useEffect, useState } from 'react';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  Title,
} from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend, Title);

interface PieChartProps {
  labels?: string[];
  data?: number[];
  title?: string;
  onSectionClick?: (index: number) => void;
}

const PieChart: React.FC<PieChartProps> = ({ labels, data, title, onSectionClick }) => {
  const [colors, setColors] = useState({
    textColor: '',
  });

  const updateColors = () => {
    const rootStyles = getComputedStyle(document.documentElement);
    setColors({
      textColor: rootStyles.getPropertyValue('--text-color').trim(),
    });
  };

  useEffect(() => {
    updateColors();

    const observer = new MutationObserver(() => {
      updateColors();
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });

    return () => observer.disconnect();
  }, []);

  const backgroundColors = [
    '#27ae60',
    '#2980b9',
    '#e67e22',
    '#8e44ad',
    '#c0392b',
    '#f1c40f',
    '#16a085',
    '#d35400',
  ];

  // Normalizar labels y registrar para depuración
  const processedLabels: string[] = Array.isArray(labels)
    ? labels
    : labels !== undefined && labels !== null
      ? [String(labels)]
      : [];

  const chartData = {
    labels: processedLabels,
    datasets: [
      {
        data,
        backgroundColor: backgroundColors,
        borderColor: '#fff',
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: colors.textColor,
        },
      },
      title: {
        display: true,
        text: title,
        color: colors.textColor,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = context.label || '';
            const value = context.raw;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
    onClick: (event: any, elements: any[]) => {
      if (onSectionClick && elements && elements.length > 0) {
        const index = elements[0].index;
        onSectionClick(index);
      }
    },
  };

  const customValuePlugin = {
    id: 'customValuePlugin',
    afterDatasetsDraw: (chart: any) => {
        const { ctx } = chart;
        const dataset = chart.data.datasets[0];
        const meta = chart.getDatasetMeta(0);
        const total = dataset.data.reduce((acc: number, val: number) => acc + val, 0);
  
        meta.data.forEach((element: any, index: number) => {
            // Verifica si la leyenda para este segmento está oculta
            const legendItem = chart.legend.legendItems[index];
            if (legendItem && legendItem.hidden) return;
  
            const value = dataset.data[index];
            const percentage = ((value / total) * 100).toFixed(1);
  
            const { x, y } = element.tooltipPosition();
            ctx.save();
            ctx.fillStyle = colors.textColor || 'white';
            ctx.font = 'bold 14px sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(`${percentage}%`, x, y);
            ctx.restore();
        });
    },
  };

  return <Pie data={chartData} options={options} plugins={[customValuePlugin]} />;
};

export default PieChart;